import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, from, firstValueFrom } from 'rxjs';
import {
  signIn,
  signOut,
  resetPassword,
  ResetPasswordOutput,
  confirmResetPassword,
  getCurrentUser,
  confirmSignIn,
  fetchAuthSession,
  ConfirmSignInOutput,
  updatePassword,
} from 'aws-amplify/auth';
import { tap, map } from 'rxjs/operators';
import { LoadingService } from '@services/loading.service';
import { defaultStorage, sessionStorage } from 'aws-amplify/utils';
// import { resendSignUpCode } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { AccountResponse } from '@entities/account';
import { SystemAdminService } from './system-admin.service';
import { AccountService } from './api/account.service';

/** ストレージ */
enum Storage {
  local = 'local',
  session = 'session',
}

export type LogInEvent =
  | 'authenticated'
  | 'mfa_required'
  | 'new_password_required';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  /** ログイン状態 */
  private _loggedIn: BehaviorSubject<boolean>;

  /** ユーザー情報 */
  private _loggedInUser: AccountResponse | null = null;

  /** 認証トークンの保存先 */
  private readonly storageKey = 'matp.auth.dest';

  /** ログインステータスの保存先 */
  private readonly statusStorageKey = 'matp.auth.loginstatus';

  // ログインしている間はemail(username)を使いまわすために内部的にusernameを保持
  private _username: string | null = null;
  private _password: string | null = null;

  constructor(
    private router: Router,
    private loadingService: LoadingService,
    private accountService: AccountService,
    private systemAdminService: SystemAdminService,
  ) {
    this._loggedIn = new BehaviorSubject<boolean>(false);
    this._initAuthStorage();
  }

  /**
   * ログインを行う。
   * @param username ユーザー名
   * @param password パスワード
   */
  login(username: string, password: string): Observable<LogInEvent> {
    const _login = async (): Promise<LogInEvent> => {
      const output = await signIn({
        username,
        password,
        options: { authFlowType: 'CUSTOM_WITH_SRP' },
      });

      switch (output.nextStep.signInStep) {
        case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
          this.loggedIn.next(false);
          this._username = username;
          this._password = password;
          return 'mfa_required';
        case 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE':
          this.loggedIn.next(false);
          this._username = username;
          this._password = password;
          return 'mfa_required';
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          this.loggedIn.next(false);
          return 'new_password_required';
        case 'DONE':
          if (output.isSignedIn) {
            await this._findLoggedInUser();
            this.loggedIn.next(true);
          }
          return 'authenticated';
        default:
          await this._findLoggedInUser();
          this.loggedIn.next(false);
          return 'authenticated';
      }
    };

    return this.loadingService.asyncWrapper(_login());
  }

  /** ログアウト */
  logout(): void {
    this.loadingService.asyncWrapper(signOut()).subscribe({
      next: () => {
        this._username = null;
        this._password = null;
        this._loggedInUser = null;
        this.loggedIn.next(false);
        localStorage.removeItem(this.storageKey);
        sessionStorage.removeItem(this.statusStorageKey);
        this.systemAdminService.clear();
        this.router.navigate(['/login']);
      },
      error: (error) => console.error(error),
    });
  }

  /** 仮パスワードの変更 */
  changeTemporaryPassword(password: string): Observable<ConfirmSignInOutput> {
    return this.loadingService
      .asyncWrapper(confirmSignIn({ challengeResponse: password }))
      .pipe(
        tap((output) => {
          if (output.isSignedIn && output.nextStep.signInStep === 'DONE') {
            this.loggedIn.next(true);
          }
        }),
      );
  }

  /** MFA認証を行う */
  certifyMFACode(code: string): Observable<ConfirmSignInOutput> {
    return this.loadingService
      .asyncWrapper(confirmSignIn({ challengeResponse: code }))
      .pipe(
        tap(() => {
          this.accountService.enableMfa().subscribe();
          this.loggedIn.next(true);
        }),
      );
  }

  /**
   * 認証メールの再送リクエストをする
   */
  requestResendMFACode(): Observable<LogInEvent> {
    return this.loadingService.asyncWrapper(
      this.login(this._username as string, this._password as string),
    );
  }

  /**
   * パスワードをリセットする
   * @param email メールアドレス
   * @returns リセット要求結果
   */
  requestPasswordReset(email: string): Observable<ResetPasswordOutput> {
    return this.loadingService.asyncWrapper(resetPassword({ username: email }));
  }

  /**
   * パスワードを変更する
   * @param oldPassword 現在のパスワード(旧パスワード)
   * @param newPassword 新しいパスワード
   * @returns
   */
  changePassword(oldPassword: string, newPassword: string): Observable<void> {
    return this.loadingService.asyncWrapper(
      updatePassword({
        oldPassword: oldPassword,
        newPassword: newPassword,
      }),
    );
  }

  /**
   * 新しいパスワードでリセット処理を行う
   * @param email メールアドレス
   * @param code 認証コード
   * @param newPassword 新しいパスワード
   * @returns リセット結果
   */
  resetPassword(
    email: string,
    code: string,
    newPassword: string,
  ): Observable<void> {
    return this.loadingService.asyncWrapper(
      confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      }),
    );
  }

  /** ログイン状態の取得 */
  isAuthenticated(): Observable<boolean> {
    const _isAuthenticated = async (): Promise<boolean> => {
      try {
        await getCurrentUser();

        if (!this._loggedInUser) {
          await this._findLoggedInUser();
        }
        this.loggedIn.next(true);
        this.setLoginStatusStorage();
        return true;
      } catch (error) {
        this.loggedIn.next(false);
        this._loggedInUser = null;
        return false;
      }
    };
    return this.loadingService.asyncWrapper(_isAuthenticated());
  }

  /** トークンを取得 */
  getAccessToken(): Observable<string | undefined> {
    return from(fetchAuthSession()).pipe(
      map((session) => session.tokens?.accessToken.toString()),
    );
  }

  /**
   * 認証トークン情報の保存先を設定する
   *
   * @param rememberMe Trueの場合、ログイン状態が保持される
   */
  setAuthStorage(rememberMe: boolean): void {
    const storage = rememberMe ? defaultStorage : sessionStorage;
    cognitoUserPoolsTokenProvider.setKeyValueStorage(storage);
    localStorage.setItem(
      this.storageKey,
      rememberMe ? Storage.local : Storage.session,
    );
  }

  /**
   * ログインステータスを設定する
   */
  setLoginStatusStorage(): void {
    if (!sessionStorage.storage?.getItem(this.statusStorageKey)) {
      sessionStorage.setItem(this.statusStorageKey, 'loggedin');
      this.accountService.postLoggedIn().subscribe({
        next: () => {},
        error: () => {
          // ログ埋め込み処理が失敗した場合でも処理を止めない。
        },
      });
    }
  }

  /** ログイン状態 */
  get loggedIn(): BehaviorSubject<boolean> {
    return this._loggedIn;
  }

  /** ログインユーザー情報 */
  get loggedInUser(): AccountResponse | null {
    return this._loggedInUser;
  }

  /**
   * 認証トークン情報の保存先を初期設定する
   */
  private _initAuthStorage(): void {
    const storageKey = localStorage.getItem(this.storageKey);
    this.setAuthStorage(storageKey == Storage.local);
  }

  /** ログインユーザーを取得 */
  private async _findLoggedInUser(): Promise<AccountResponse> {
    const account = await firstValueFrom(
      this.accountService.findLoggedInUser(),
    );
    this._loggedInUser = account;
    return account;
  }
}
